import { create } from 'zustand';
import {
  settingsService,
  type User,
  type UserFilter,
  type CreateUserDto,
  type UpdateUserDto,
  type UpdateProfileDto,
  type Role,
  type CreateRoleDto,
  type UpdateRoleDto,
  type NotificationPreferences,
} from '@services/index';
import { setUserTimeZone } from '@shared/format/userTimeZone';

interface SettingsState {
  // Users data
  users: User[];
  user: User | null;
  profile: User | null;

  // Roles data
  roles: Role[];
  role: Role | null;
  availablePermissions: string[];

  // Notification preferences
  notificationPreferences: NotificationPreferences | null;

  // Pagination
  total: number;
  page: number;
  limit: number;
  totalPages: number;

  // Filters
  filters: UserFilter;

  // Loading states
  isLoading: boolean;
  isLoadingUser: boolean;
  isLoadingRoles: boolean;
  isLoadingProfile: boolean;
  isSaving: boolean;
  error: string | null;

  // User actions
  fetchUsers: (filter?: UserFilter) => Promise<void>;
  fetchUser: (id: string) => Promise<void>;
  createUser: (dto: CreateUserDto) => Promise<User>;
  updateUser: (id: string, dto: UpdateUserDto) => Promise<User>;
  deleteUser: (id: string) => Promise<void>;
  activateUser: (id: string) => Promise<void>;
  deactivateUser: (id: string) => Promise<void>;

  // Profile actions
  fetchProfile: () => Promise<void>;
  updateProfile: (dto: UpdateProfileDto) => Promise<User>;

  // Role actions
  fetchRoles: () => Promise<void>;
  fetchRole: (id: string) => Promise<void>;
  fetchAvailablePermissions: () => Promise<void>;
  createRole: (dto: CreateRoleDto) => Promise<Role>;
  updateRole: (id: string, dto: UpdateRoleDto) => Promise<Role>;
  deleteRole: (id: string) => Promise<void>;

  // Notification preferences actions
  fetchNotificationPreferences: () => Promise<void>;
  updateNotificationPreferences: (prefs: Partial<NotificationPreferences>) => Promise<void>;

  // Filter actions
  setFilters: (filters: UserFilter) => void;
  setPage: (page: number) => void;

  // Reset actions
  reset: () => void;
  resetUser: () => void;
}

const initialState = {
  users: [],
  user: null,
  profile: null,
  roles: [],
  role: null,
  availablePermissions: [],
  notificationPreferences: null,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  filters: {},
  isLoading: false,
  isLoadingUser: false,
  isLoadingRoles: false,
  isLoadingProfile: false,
  isSaving: false,
  error: null,
};

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  ...initialState,

  // ============== USER ACTIONS ==============

  fetchUsers: async (filter) => {
    const currentFilters = filter || get().filters;
    set({ isLoading: true, error: null, filters: currentFilters });

    try {
      const response = await settingsService.getUsers({
        ...currentFilters,
        page: currentFilters.page || get().page,
        limit: currentFilters.limit || get().limit,
      });

      set({
        users: response.data,
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch users',
        isLoading: false,
      });
    }
  },

  fetchUser: async (id) => {
    set({ isLoadingUser: true, error: null });

    try {
      const user = await settingsService.getUser(id);
      set({ user, isLoadingUser: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch user',
        isLoadingUser: false,
      });
    }
  },

  createUser: async (dto) => {
    set({ isSaving: true, error: null });

    try {
      const user = await settingsService.createUser(dto);
      set((state) => ({
        users: [user, ...state.users],
        total: state.total + 1,
        isSaving: false,
      }));
      return user;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create user',
        isSaving: false,
      });
      throw error;
    }
  },

  updateUser: async (id, dto) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.updateUser(id, dto);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updated : u)),
        user: state.user?.id === id ? updated : state.user,
        isSaving: false,
      }));
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update user',
        isSaving: false,
      });
      throw error;
    }
  },

  deleteUser: async (id) => {
    set({ isSaving: true, error: null });

    try {
      await settingsService.deleteUser(id);
      set((state) => ({
        users: state.users.filter((u) => u.id !== id),
        total: state.total - 1,
        user: state.user?.id === id ? null : state.user,
        isSaving: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete user',
        isSaving: false,
      });
      throw error;
    }
  },

  activateUser: async (id) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.activateUser(id);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updated : u)),
        user: state.user?.id === id ? updated : state.user,
        isSaving: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to activate user',
        isSaving: false,
      });
      throw error;
    }
  },

  deactivateUser: async (id) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.deactivateUser(id);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updated : u)),
        user: state.user?.id === id ? updated : state.user,
        isSaving: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to deactivate user',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== PROFILE ACTIONS ==============

  fetchProfile: async () => {
    set({ isLoadingProfile: true, error: null });

    try {
      const profile = await settingsService.getProfile();
      set({ profile, isLoadingProfile: false });
      // Seed the central formatter's timezone so all displayed times honour the
      // user's Settings → Account → Timezone preference (#18929).
      setUserTimeZone((profile?.preferences as Record<string, string> | undefined)?.timezone);
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch profile',
        isLoadingProfile: false,
      });
    }
  },

  updateProfile: async (dto) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.updateProfile(dto);
      set({ profile: updated, isSaving: false });
      // Apply the (possibly changed) timezone immediately so times re-render in
      // the new zone without a reload (#18929).
      setUserTimeZone((updated?.preferences as Record<string, string> | undefined)?.timezone);
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update profile',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== ROLE ACTIONS ==============

  fetchRoles: async () => {
    set({ isLoadingRoles: true });

    try {
      const roles = await settingsService.getRoles();
      set({ roles, isLoadingRoles: false });
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      set({ isLoadingRoles: false });
    }
  },

  fetchRole: async (id) => {
    try {
      const role = await settingsService.getRole(id);
      set({ role });
    } catch (error) {
      console.error('Failed to fetch role:', error);
    }
  },

  fetchAvailablePermissions: async () => {
    try {
      const availablePermissions = await settingsService.getAvailablePermissions();
      set({ availablePermissions });
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  },

  createRole: async (dto) => {
    set({ isSaving: true, error: null });

    try {
      const role = await settingsService.createRole(dto);
      set((state) => ({
        roles: [...state.roles, role],
        isSaving: false,
      }));
      return role;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create role',
        isSaving: false,
      });
      throw error;
    }
  },

  updateRole: async (id, dto) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.updateRole(id, dto);
      set((state) => ({
        roles: state.roles.map((r) => (r.id === id ? updated : r)),
        role: state.role?.id === id ? updated : state.role,
        isSaving: false,
      }));
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update role',
        isSaving: false,
      });
      throw error;
    }
  },

  deleteRole: async (id) => {
    set({ isSaving: true, error: null });

    try {
      await settingsService.deleteRole(id);
      set((state) => ({
        roles: state.roles.filter((r) => r.id !== id),
        role: state.role?.id === id ? null : state.role,
        isSaving: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete role',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== NOTIFICATION PREFERENCES ACTIONS ==============

  fetchNotificationPreferences: async () => {
    try {
      const notificationPreferences = await settingsService.getNotificationPreferences();
      set({ notificationPreferences });
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  },

  updateNotificationPreferences: async (prefs) => {
    set({ isSaving: true, error: null });

    try {
      const updated = await settingsService.updateNotificationPreferences(prefs);
      set({ notificationPreferences: updated, isSaving: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update preferences',
        isSaving: false,
      });
      throw error;
    }
  },

  // ============== FILTER ACTIONS ==============

  setFilters: (filters) => {
    set({ filters, page: 1 });
    get().fetchUsers({ ...filters, page: 1 });
  },

  setPage: (page) => {
    set({ page });
    get().fetchUsers({ ...get().filters, page });
  },

  // ============== RESET ACTIONS ==============

  reset: () => {
    set(initialState);
  },

  resetUser: () => {
    set({ user: null, isLoadingUser: false });
  },
}));
